import React, { useState, useEffect } from 'react';
import {
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  GiftOutlined,
  UserOutlined,
  CheckCircleOutlined,
  DownOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { useSearchParams } from 'react-router-dom';
import { giftService, Gift, Event, GiftGiver } from '../../../services/giftService';
import { NotiModal } from '../../../components/Noti/NotiModal';
import styles from './GiftsPageMobile.module.css';
import { GiftDetailModalMobile } from '../../../components/gifts/GiftDetailModalMobile';
import { AddGiftsModalMobile } from '../../../components/gifts/AddGiftsModalMobile';
import { FilterModalMobile } from '../../../components/gifts/FilterModalMobile';



// Cấu hình locale tiếng Việt
dayjs.locale('vi');

export const GiftsPageMobile: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [givers, setGivers] = useState<GiftGiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGift, setEditingGift] = useState<Gift | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [giftToDelete, setGiftToDelete] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    event_id: undefined as number | undefined,
    giver_id: undefined as number | undefined,
    gift_type: undefined as string | undefined,
    is_returned: undefined as boolean | undefined,
    search: '',
  });
  const [showEventFilter, setShowEventFilter] = useState(false);
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const eventId = searchParams.get('event_id');
  const giverId = searchParams.get('giver_id');

  const fetchGifts = async (reset = false) => {
    try {
      // Xác định trang cần load
      const pageToLoad = reset ? 1 : currentPage;
      
      if (reset) {
      setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const params: any = {
        page: pageToLoad,
        limit: pageSize,
        ...filters,
      };
      if (eventId) params.event_id = parseInt(eventId);
      if (giverId) params.giver_id = parseInt(giverId);
      
      const response = await giftService.getGifts(params);
      
      // Cập nhật dữ liệu
      if (reset) {
      setGifts(response.data);
        setCurrentPage(1);
      } else {
        // Merge data mới với data cũ, tránh trùng lặp
        setGifts(prev => {
          const existingIds = new Set(prev.map(gift => gift.id));
          const newGifts = response.data.filter(gift => !existingIds.has(gift.id));
          return [...prev, ...newGifts];
        });
      }
      
      // Cập nhật pagination info
      const pagination = (response as any).pagination;
      if (pagination) {
        setTotalCount(pagination.total);
        setHasMore(pageToLoad < pagination.totalPages);
      } else {
        setHasMore(response.data.length === pageSize);
      }
    } catch (error) {
      console.error('Error fetching gifts:', error);
      message.error('Không thể tải danh sách tiền mừng');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more function
  const loadMore = () => {
    if (!loadingMore && hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await giftService.getEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchGivers = async () => {
    try {
      const response = await giftService.getGiftGivers({ limit: 1000 }); // Lấy tất cả givers
      setGivers(response.data);
    } catch (error) {
      console.error('Error fetching givers:', error);
    }
  };

  // Effect để load dữ liệu khi page thay đổi
  useEffect(() => {
    if (currentPage === 1) {
      fetchGifts(true); // Reset data khi về trang 1
    } else {
      fetchGifts(false); // Load more khi trang > 1
    }
  }, [currentPage]);

  // Effect để reset pagination khi filter thay đổi
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setGifts([]);
    setTotalCount(0);
    // Fetch dữ liệu mới với filter mới
    setTimeout(() => {
      fetchGifts(true);
    }, 100);
  }, [filters, eventId, giverId]);

  useEffect(() => {
    // Load events và givers trước
    const loadData = async () => {
      try {
        await Promise.all([fetchEvents(), fetchGivers()]);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const handleCreate = () => {
    setEditingGift(null);
    setModalVisible(true);
  };

  const handleEdit = (record: Gift) => {
    setEditingGift(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await giftService.deleteGift(id);
      message.success('Xóa tiền mừng thành công');
      fetchGifts();
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      const errorMessage = error.response?.data?.error || error.message || 'Không thể xóa tiền mừng';
      message.error(errorMessage);
    }
  };

  const handleShowDeleteModal = (giftId: number) => {
    setGiftToDelete(giftId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (giftToDelete) {
      await handleDelete(giftToDelete);
      setShowDeleteModal(false);
      setGiftToDelete(null);
    }
  };

  const handleModalSuccess = () => {
    fetchGifts(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getEventName = (eventId: number, eventName?: string) => {
    // Ưu tiên sử dụng event_name từ API response
    if (eventName) return eventName;
    
    const event = events.find(e => e.id === eventId);
    if (!event) {
      // Nếu events chưa load xong, hiển thị loading
      if (events.length === 0) return 'Đang tải...';
      return 'Sự kiện không tồn tại';
    }
    return event.name;
  };

  const getGiverName = (giverId: number, giverName?: string) => {
    // Ưu tiên sử dụng giver_name từ API response
    if (giverName) return giverName;
    
    const giver = givers.find(g => g.id === giverId);
    if (!giver) {
      // Nếu givers chưa load xong, hiển thị loading
      if (givers.length === 0) return 'Đang tải...';
      return 'Người mừng không tồn tại';
    }
    return giver.full_name;
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value 
    }));
  };

  // Throttle scroll để tránh gọi quá nhiều
  const handleScroll = React.useCallback(
    React.useMemo(() => {
      let isThrottled = false;
      return (e: React.UIEvent<HTMLDivElement>) => {
        if (isThrottled) return;
        
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        
        // Trigger load more khi scroll đến 80%
        if (scrollPercentage > 0.8) {
          loadMore();
          
          isThrottled = true;
          setTimeout(() => {
            isThrottled = false;
          }, 500); // Throttle 500ms
        }
      };
    }, [hasMore, loadingMore, loading]),
    [hasMore, loadingMore, loading]
  );

  const handleListHeaderClick = () => {
    setShowEventFilter(!showEventFilter);
  };

  const handleEventSelect = (eventId: number | undefined) => {
    handleFilterChange('event_id', eventId);
    setShowEventFilter(false);
  };

  const getSelectedEventName = () => {
    if (!filters.event_id) return 'Tất cả sự kiện';
    const event = events.find(e => e.id === filters.event_id);
    return event?.name || 'Tất cả sự kiện';
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilterApply = (newFilters: any) => {
    setFilters(newFilters);
    // Reset pagination khi áp dụng filter mới
    setCurrentPage(1);
    setHasMore(true);
    setGifts([]);
    setTotalCount(0);
    // Fetch dữ liệu mới với filter mới
    setTimeout(() => {
      fetchGifts(true);
    }, 100);
  };

  const getFilterCount = () => {
    let count = 0;
    if (filters.event_id) count++;
    if (filters.giver_id) count++;
    if (filters.gift_type) count++;
    if (filters.is_returned !== undefined) count++;
    return count;
  };

  // Nhóm tiền mừng theo ngày
  const groupGiftsByDate = () => {
    const groups: { [key: string]: Gift[] } = {};
    
    // Sort gifts by created_at DESC to maintain order
    const sortedGifts = [...gifts].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    
    sortedGifts.forEach(gift => {
      // Ưu tiên sử dụng event_date từ gift nếu có, nếu không tìm từ events
      let date = 'Không xác định';
      if (gift.event_date) {
        date = dayjs(gift.event_date).format('DD/MM/YYYY');
      } else {
        const event = events.find(e => e.id === gift.event_id);
        if (event) {
          date = dayjs(event.event_date).format('DD/MM/YYYY');
        }
      }
      
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(gift);
    });

    return Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Không xác định') return 1;
      if (b === 'Không xác định') return -1;
      return dayjs(b, 'DD/MM/YYYY').diff(dayjs(a, 'DD/MM/YYYY'));
    });
  };

  // Khi click vào card quà mừng
  const handleGiftCardClick = (gift: Gift) => {
    setSelectedGift(gift);
    setIsDetailModalOpen(true);
  };

  // Sửa từ modal chi tiết
  const handleDetailEdit = (gift: Gift) => {
    setIsDetailModalOpen(false);
    setTimeout(() => {
      handleEdit(gift);
    }, 300); // Đợi Drawer đóng
  };

  // Xóa từ modal chi tiết
  const handleDetailDelete = (gift: Gift) => {
    setIsDetailModalOpen(false);
    setTimeout(() => {
      handleShowDeleteModal(gift.id);
    }, 300);
  };

  const renderGiftCard = (gift: Gift) => {
    
    return (
      <div key={gift.id} className={styles.giftCard} onClick={() => handleGiftCardClick(gift)} style={{ cursor: 'pointer' }}>
        <div className={styles.iconContainer}>
          <GiftOutlined />
        </div>
        
        <div className={styles.giftDetails}>
          <p className={styles.giftTitle}>
            <UserOutlined /> {getGiverName(gift.giver_id, gift.giver_name)}
          </p>
          <span className={styles.giftInfo}>
            {getEventName(gift.event_id, gift.event_name)}
          </span>
          {gift.notes && (
            <span className={styles.giftNotes}>
              {gift.notes}
            </span>
          )}
        </div>
        
        <div className={styles.giftAmount}>
          <p className={styles.amount}>
            {formatCurrency(gift.amount)}
          </p>
          <Tag 
            color={gift.is_returned ? 'green' : 'orange'} 
            icon={gift.is_returned ? <CheckCircleOutlined /> : null}
            className={styles.returnedTag}
          >
            {gift.is_returned ? 'Đã đáp lễ' : 'Chưa đáp lễ'}
          </Tag>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <GiftOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có tiền mừng nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm tiền mừng đầu tiên của bạn
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* Gift List */}
        <main className={styles.giftList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft} onClick={handleListHeaderClick}>
              <h3>{getSelectedEventName()}</h3>
              <DownOutlined style={{ 
                transform: showEventFilter ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleCreate}>
                <PlusOutlined />
              </div>
              <div className={styles.filterButton} onClick={handleFilterModalOpen}>
                <FilterOutlined />
                {getFilterCount() > 0 && (
                  <span className={styles.filterBadge}>{getFilterCount()}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Event Filter Dropdown */}
          {showEventFilter && (
            <div className={styles.eventFilterDropdown}>
              <div 
                className={styles.eventFilterOption}
                onClick={() => handleEventSelect(undefined)}
              >
                <span>Tất cả sự kiện</span>
              </div>
              {events.map((event) => (
                <div 
                  key={event.id}
                  className={styles.eventFilterOption}
                  onClick={() => handleEventSelect(event.id)}
                >
                  <span>{event.name} - {dayjs(event.event_date).format('DD/MM/YYYY')}</span>
                </div>
              ))}
            </div>
          )}

          {/* Scrollable Gift Content */}
          <div className={styles.giftContent} onScroll={handleScroll}>
            {gifts.length > 0 ? (
              <>
                {groupGiftsByDate().map(([date, gifts]) => (
                  <div key={date} className={styles.giftGroup}>
                    <p className={styles.groupDate}>{date}</p>
                    {gifts.map(renderGiftCard)}
            </div>
                ))}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Đang tải thêm...</span>
                  </div>
                )}
                {!hasMore && gifts.length > 0 && (
                  <div className={styles.noMoreData}>
                    <span>Đã tải hết dữ liệu ({gifts.length}/{totalCount} mục)</span>
                  </div>
                )}

              </>
            ) : (
              renderEmptyState()
            )}
          </div>
        </main>


      </div>
      {/* Modal chi tiết quà mừng */}
      <GiftDetailModalMobile
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        gift={selectedGift}
        event={selectedGift ? events.find(e => e.id === selectedGift.event_id) : undefined}
        giver={selectedGift ? givers.find(g => g.id === selectedGift.giver_id) : undefined}
        onEdit={handleDetailEdit}
        onDelete={handleDetailDelete}
      />
      {/* Modal thêm/sửa tiền mừng */}
      <AddGiftsModalMobile
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
        editingGift={editingGift}
        eventId={eventId}
        giverId={giverId}
      />

      {/* Modal bộ lọc */}
      <FilterModalMobile
        isOpen={isFilterModalOpen}
        onClose={handleFilterModalClose}
        onApply={handleFilterApply}
        currentFilters={filters}
        events={events}
        givers={givers}
      />

      {/* Modal xác nhận xóa */}
      <NotiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa tiền mừng này?"
        confirmText="Xóa"
        cancelText="Hủy"
      />
    </div>
  );
};
